/*!
 * Copyright 2024 - Swiss Data Science Center (SDSC)
 * A partnership between École Polytechnique Fédérale de Lausanne (EPFL) and
 * Eidgenössische Technische Hochschule Zürich (ETHZ).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export const ABSOLUTE_ROUTES = {
  root: "/",
  /** @deprecated Renku Legacy route */
  datasets: {
    root: "/datasets",
    splat: "/datasets/*",
  },
  /** @deprecated Renku Legacy route */
  projects: { splat: "/projects/*" },
  /** @deprecated Renku Legacy routes */
  v1: { splat: "/v1/*" },
  v2: {
    index: "/",
    admin: "/admin",
    dataConnectors: {
      root: "/d/:slug",
      show: {
        root: "/d/:projectNamespace?/:dataConnectorNamespace?/:slug",
        settings:
          "/d/:projectNamespace?/:dataConnectorNamespace?/:slug/settings",
      },
    },
    groups: {
      show: {
        root: "/g/:slug",
        settings: "/g/:slug/settings",
        search: "/g/:slug/search",
        splat: "/g/:slug/*",
      },
      beta: { splat: "/v2/groups/:slug/*" },
    },
    help: {
      root: "/help",
      status: "/help/status",
      release: "/help/release",
      tos: "/help/tos",
      privacy: "/help/privacy",
    },
    integrations: {
      root: "/integrations",
      complete: "/integrations/complete",
    },
    ssh_keys: "/ssh_keys",
    projects: {
      show: {
        root: "/p/:namespace/:slug",
        settings: "/p/:namespace/:slug/settings",
        apps: {
          root: "/p/:namespace/:slug/apps",
          show: "/p/:namespace/:slug/apps/:launcherId",
        },
        sessions: {
          root: "/p/:namespace/:slug/sessions",
          show: "/p/:namespace/:slug/sessions/show/:session",
          start: "/p/:namespace/:slug/sessions/:launcherId/start",
        },
      },
      showById: "/p/:id",
      beta: { splat: "/v2/projects/:namespace/:slug/*" },
    },
    search: "/search",
    secrets: "/secrets",
    sessions: {
      show: "/s/:id",
    },
    user: "/user",
    users: {
      show: {
        root: "/u/:username",
        search: "/u/:username/search",
      },
    },
  },
} as const;

export const RELATIVE_ROUTES = {
  root: "/",
  /** @deprecated Renku Legacy route */
  datasets: {
    root: "datasets",
    splat: "*",
  },
  /** @deprecated Renku Legacy route */
  projects: { splat: "projects/*" },
  /** @deprecated Renku Legacy routes */
  v1: { splat: "v1/*" },
  v2: {
    index: "/",
    admin: "admin",
    betaRoot: "/v2/*",
    dataConnectors: {
      root: "d",
      show: {
        root: ":projectNamespace?/:dataConnectorNamespace?/:slug/*",
        settings: "settings",
      },
    },
    groups: {
      root: "g",
      show: {
        root: ":slug",
        search: "search",
        settings: "settings",
      },
    },
    help: {
      root: "help",
      status: "status",
      release: "release",
      tos: "tos",
      privacy: "privacy",
    },
    integrations: {
      root: "integrations",
      complete: "complete",
    },
    ssh_keys: "ssh_keys",
    projects: {
      root: "p/*",
      show: {
        root: ":namespace/:slug/*",
        settings: "settings",
        apps: {
          root: "apps/*",
          show: ":launcherId",
        },
        sessions: {
          root: "sessions/*",
          show: "show/:session",
          start: ":launcherId/start",
        },
      },
      showById: ":id",
    },
    search: "search",
    secrets: "secrets",
    sessions: {
      root: "s",
      show: ":id",
    },
    user: "user",
    users: {
      root: "u",
      show: {
        root: ":username",
        search: "search",
      },
    },
  },
} as const;
